import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Calendar, Users, ShieldCheck, Flag, ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import { formatAuDate } from "@/lib/date";

export default function Landing() {
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  useEffect(() => {
    api.get("/public/events").then((r) => setEvents(r.data || [])).catch(() => setEvents([])).finally(() => setLoadingEvents(false));
  }, []);
  return (
    <div className="min-h-screen bg-white text-[#0A0A0B]">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-black/10 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" className="font-display text-2xl font-black tracking-tighter" data-testid="brand-logo">
            VOLMEE<span className="text-[#FF3B30]">.</span>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <a href="#events" className="text-zinc-600 hover:text-black transition-colors" data-testid="nav-events-link">Live events</a>
            <Link to="/admin/login" className="text-zinc-600 hover:text-black transition-colors" data-testid="admin-login-link">
              Organiser sign-in
            </Link>
            <Link to="/admin/login" data-testid="hero-register-btn" className="brand-btn inline-flex items-center gap-2 px-4 py-2 text-sm font-medium">
              Create an event <ArrowRight className="h-4 w-4" />
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{
          backgroundColor: "#0A0A0B",
          backgroundImage:
            "linear-gradient(to right, rgba(10,10,11,0.85) 0%, rgba(10,10,11,0.65) 45%, rgba(10,10,11,0.35) 100%), url(https://customer-assets.emergentagent.com/job_volmee-allocate/artifacts/5wr4zdv7_vols.webp)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="pointer-events-none absolute -right-24 top-10 h-64 w-64 rotate-12 bg-[#FF3B30]/25 blur-3xl" />
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-24 md:py-32 md:grid-cols-12">
          <div className="md:col-span-7">
            <h1 className="font-display text-5xl font-black leading-[0.95] tracking-tighter text-white md:text-7xl">
              <span className="text-[#FF3B30]">Officials</span> in position.
            </h1>
            <p className="mt-6 max-w-xl text-base text-white md:text-lg [text-shadow:_0_2px_12px_rgb(0_0_0_/_0.75)]">
              VOLMEE is the volunteer operations backbone for large motorsport events. Build each rally day on its own — different sessions, different roles — then send one link. Volunteers pick every position they want in a single itinerary.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href="#events" data-testid="hero-cta-events" className="brand-btn inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold">
                Browse live events <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/admin/login" data-testid="hero-cta-admin" className="inline-flex items-center gap-2 border border-white/40 bg-transparent px-6 py-3 text-sm font-semibold text-white hover:bg-white hover:text-black">
                Organiser sign-in
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PUBLIC EVENTS — tile grid */}
      <section id="events" className="border-t border-black/10" data-testid="public-events-section">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#002FA7]">Live now</div>
              <h2 className="mt-1 font-display text-4xl font-black tracking-tighter md:text-5xl">Events looking for volunteers.</h2>
            </div>
            {events.length > 0 ? <span className="font-mono text-xs text-zinc-500" data-testid="public-events-count">{events.length} live · updated in real time</span> : null}
          </div>

          {loadingEvents ? (
            <p className="text-sm text-zinc-500">Loading…</p>
          ) : events.length === 0 ? (
            <div className="border border-dashed border-black/15 bg-[#F9F9FB] p-14 text-center" data-testid="public-events-empty">
              <p className="font-display text-2xl font-black tracking-tighter">No public events at the moment.</p>
              <p className="mt-2 text-sm text-zinc-500">Check back soon — or if you're an organiser, sign in above to create one.</p>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="public-events-grid">
              {events.map((ev) => {
                const dates = (ev.days || []).map(formatAuDate);
                const range = dates.length === 0 ? "TBA" : dates.length === 1 ? dates[0] : dates.length === 2 ? `${dates[0]} + ${dates[1]}` : `${dates[0]} – ${dates[dates.length - 1]}`;
                return (
                  <Link
                    key={ev.id}
                    to={`/events/${ev.slug}`}
                    data-testid={`event-tile-${ev.slug}`}
                    className="group relative flex aspect-square flex-col justify-end overflow-hidden border border-black/15 bg-white shadow-[8px_8px_0_0_rgba(0,0,0,0.08)] transition-all hover:-translate-y-1 hover:shadow-[12px_12px_0_0_rgba(0,47,167,0.15)]"
                  >
                    {ev.cover_image ? (
                      <img
                        src={ev.cover_image.url || `/api/public/events/${ev.slug}/cover-image`}
                        alt={ev.name}
                        className="absolute inset-0 h-full w-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#F4F4F5] to-[#E4E4E7]">
                        <ImageIcon className="h-10 w-10 text-zinc-400" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-transparent" />
                    <div className="relative p-5 text-white">
                      <div className="text-[10px] font-medium uppercase tracking-widest text-white/80" data-testid={`event-tile-dates-${ev.slug}`}>{range}</div>
                      <div className="mt-1 font-display text-2xl font-black leading-tight tracking-tighter [text-shadow:_0_1px_10px_rgb(0_0_0_/_0.8)]" data-testid={`event-tile-name-${ev.slug}`}>{ev.name}</div>
                      {ev.description ? <div className="mt-1 line-clamp-2 text-xs text-white/90 [text-shadow:_0_1px_8px_rgb(0_0_0_/_0.85)]">{ev.description}</div> : null}
                      <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-white group-hover:text-[#FF3B30]">
                        Volunteer for this event <ArrowRight className="h-3.5 w-3.5" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-black/10">
        <div className="mx-auto grid max-w-7xl gap-0 px-0 md:grid-cols-3">
          {[
            { icon: Calendar, title: "Day-by-day build", copy: "Every event day gets its own sessions and roles. Change Sunday's stages without touching Saturday's spread." },
            { icon: Users, title: "Multi-slot itinerary", copy: "Volunteers add every stage and role they want in one sitting. VOLMEE confirms each — or suggests the next best." },
            { icon: ShieldCheck, title: "Confirmations that stick", copy: "Bot-checked signup, email + SMS confirmations, drag-and-drop reassign. No stage ever goes unmanned." },
          ].map((f) => (
            <div key={f.title} className="border-r border-b border-black/10 p-10 md:p-12">
              <f.icon className="h-8 w-8 text-[#002FA7]" />
              <h3 className="mt-6 font-display text-2xl font-black tracking-tighter">{f.title}</h3>
              <p className="mt-3 text-sm text-zinc-600">{f.copy}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-black/10 py-8">
        <div className="mx-auto max-w-7xl px-6 text-xs text-zinc-500">
          © 2026 VOLMEE. Built for large events.
        </div>
      </footer>
    </div>
  );
}
